import { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { format, startOfDay, endOfDay } from 'date-fns';
import { getCustomerCodes, getDeliveredShipments, CustomerOption, getBranches, BranchOption, getDeliveredAlerts } from '../api/shipments';
import { DataTable } from 'components/ui/data-table';
import { deliveredColumns } from './columns';
import { useBreadcrumbs } from 'contexts/BreadcrumbsContext';

interface DateRange {
  from?: Date;
  to?: Date;
}

interface PaginationState {
  pageIndex: number;
  pageSize: number;
}

interface SortingState {
  id: string;
  desc: boolean;
}

interface AlertFilter {
  pod_not_received?: string;
  pending_invoice?: string;
  cust_id?: string;
}

interface AlertBucket {
  label: string;
  count: number;
  filters: AlertFilter;
}

interface AlertCard {
  id: string;
  title: string;
  type: 'bucketed' | 'single';
  style: 'positive' | 'negative' | 'neutral' | 'total';
  count?: number;
  filters?: AlertFilter;
  buckets?: AlertBucket[];
}

const Delivered = () => {
  const { setBreadcrumbs } = useBreadcrumbs();
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");
  const [searchParams, setSearchParams] = useSearchParams();
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 20,
  });
  const [pageCount, setPageCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [sorting, setSorting] = useState<SortingState[]>([{ id: 'cn_date', desc: true }]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [searchDebounceTimer, setSearchDebounceTimer] = useState<NodeJS.Timeout | null>(null);
  const [dateRange, setDateRange] = useState<DateRange>({ from: undefined, to: undefined });

  const [customerOptions, setCustomerOptions] = useState<CustomerOption[]>([]);
  const [selectedCustId, setSelectedCustId] = useState<string | undefined>(undefined);
  const [branchOptions, setBranchOptions] = useState<BranchOption[]>([]);
  const [selectedOuCode, setSelectedOuCode] = useState<string | undefined>(undefined);

  const [alertCards, setAlertCards] = useState<AlertCard[]>([]);
  const [isLoadingAlerts, setIsLoadingAlerts] = useState(false);

  const [activeAlertFilters, setActiveAlertFilters] = useState<AlertFilter>({});

  // Set breadcrumbs
  useEffect(() => {
    setBreadcrumbs([
      { label: 'Shipments', href: '#' },
      { label: 'Delivered' }
    ]);
  }, [setBreadcrumbs]);

  useEffect(() => {
    const next = new URLSearchParams();

    next.set('page', String(pagination.pageIndex + 1));
    next.set('per_page', String(pagination.pageSize));

    if (sorting.length > 0) {
      next.set('sort_by', sorting[0].id);
      next.set('sort_order', sorting[0].desc ? 'desc' : 'asc');
    }

    if (globalFilter) next.set('search', globalFilter);
    if (selectedCustId) next.set('customer', selectedCustId);
    if (selectedOuCode) next.set('branch', selectedOuCode);
    if (dateRange.from) next.set('from_date', format(dateRange.from, 'yyyy-MM-dd'));
    if (dateRange.to) next.set('to_date', format(dateRange.to, 'yyyy-MM-dd'));

    // 🟢 Add your active alert filters
    if (activeAlertFilters.pod_not_received) next.set('pod_not_received', activeAlertFilters.pod_not_received);
    if (activeAlertFilters.pending_invoice) next.set('pending_invoice', activeAlertFilters.pending_invoice);

    setSearchParams(next, { replace: true });
  }, [
    pagination.pageIndex,
    pagination.pageSize,
    sorting,
    globalFilter,
    selectedCustId,
    selectedOuCode,
    dateRange,
    activeAlertFilters,
    setSearchParams
  ]);

  const fetchCustomerData = useCallback(async () => {
      try {
        const params: any = {};
        
        if (globalFilter) {
          params.search = globalFilter;
        }
        if (dateRange.from) {
          params.from = format(startOfDay(dateRange.from), 'yyyy-MM-dd HH:mm:ss');
        }
        if (dateRange.to) {
          params.to = format(endOfDay(dateRange.to), 'yyyy-MM-dd HH:mm:ss');
        }

        params.status = "delivered";
  
        const response = await getCustomerCodes(params);
  
        if (response && response.length > 0) {
          const options: CustomerOption[] = response
            .filter(item => item?.cust_code)
            .map(item => ({
              cust_code: item.cust_code,
              cust_name: item.cust_name || item.cust_code,
            }))
            .sort((a, b) => a.cust_name.localeCompare(b.cust_name));

          setCustomerOptions(options);
        } else {
          setCustomerOptions([]);
        }
  
      } catch (error) {
        console.error("Error fetching customer data for dropdown:", error);
        setCustomerOptions([]);
      }
    }, [globalFilter, dateRange]);

  const fetchBranchData = useCallback(async () => {
    try {
      const branchParams: any = {};
      
      if (globalFilter) {
        branchParams.search = globalFilter;
      }
      if (dateRange.from) {
        branchParams.from = format(startOfDay(dateRange.from), 'yyyy-MM-dd HH:mm:ss');
      }
      if (dateRange.to) {
        branchParams.to = format(endOfDay(dateRange.to), 'yyyy-MM-dd HH:mm:ss');
      }

      branchParams.status = "delivered";

      const branches = await getBranches(branchParams);
      setBranchOptions(branches.sort((a, b) => a.branch_name.localeCompare(b.branch_name)));

    } catch (error) {
      console.error('Error fetching branch data for dropdown:', error);
      setBranchOptions([]);
    }
  }, [globalFilter, dateRange]);

    const fetchAlerts = useCallback(async () => {
      setIsLoadingAlerts(true);
      try {
        const params: any = {};
        if (globalFilter) params.search = globalFilter;
        if (dateRange.from) params.from = format(startOfDay(dateRange.from), 'yyyy-MM-dd HH:mm:ss');
        if (dateRange.to) params.to = format(endOfDay(dateRange.to), 'yyyy-MM-dd HH:mm:ss');
        if (selectedCustId) params.cust_id = selectedCustId;

        const response = await getDeliveredAlerts(params);
        setAlertCards(response.cards || []);
      } catch (error) {
        console.error('Error fetching delivered alerts:', error);
        setAlertCards([]);
      } finally {
        setIsLoadingAlerts(false);
      }
    }, [globalFilter, dateRange, selectedCustId]);

  const fetchShipments = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = {
        page: pagination.pageIndex + 1,
        per_page: pagination.pageSize,
        search: globalFilter,
        sort_by: sorting[0]?.id || 'cn_date',
        sort_order: sorting[0]?.desc ? 'desc' : 'asc',
      };

      if (dateRange.from) {
        params.from = format(startOfDay(dateRange.from), 'yyyy-MM-dd HH:mm:ss');
      }
      if (dateRange.to) {
        params.to = format(endOfDay(dateRange.to), 'yyyy-MM-dd HH:mm:ss');
      }

      if (selectedCustId) {
        params.cust_id = selectedCustId;
      }
      if (selectedOuCode) {
        params.booking_branch = selectedOuCode;
      }

      if(activeAlertFilters.pod_not_received) {
        params.pod_not_received = activeAlertFilters.pod_not_received;
      }

      if(activeAlertFilters.pending_invoice) {
        params.pending_invoice = activeAlertFilters.pending_invoice;
      }

      const response = await getDeliveredShipments(params);
      
      setShipments(response.items || []);
      setTotalCount(response.total_items || 0);
      setPageCount(response.total_pages || 0);
      setError("");
    } catch (error: any) {
      console.error('Error fetching delivered shipments:', error);
      setShipments([]);
      setTotalCount(0);
      setError(error?.response?.data?.detail || error?.message || "Failed to fetch shipments");
    } finally {
      setLoading(false);
    }
  }, [pagination.pageIndex, pagination.pageSize, globalFilter, sorting, dateRange, selectedCustId, selectedOuCode, activeAlertFilters]);

  // Debounced search effect
  useEffect(() => {
    if (searchDebounceTimer) {
      clearTimeout(searchDebounceTimer);
    }
    
    const timer = setTimeout(() => {
      setPagination(prev => ({ ...prev, pageIndex: 0 }));
    }, 300);
    
    setSearchDebounceTimer(timer);
    
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [globalFilter]);

    useEffect(() => {
      fetchCustomerData();
    }, [fetchCustomerData]);

    useEffect(() => {
      fetchBranchData();
    }, [fetchBranchData]); 

    useEffect(() => {
      fetchAlerts();
    }, [fetchAlerts]); 

  // Fetch shipments when pagination or sorting changes (including after search reset)
  useEffect(() => {
    fetchShipments();
  }, [fetchShipments]);

  const handlePaginationChange = (updater: any) => {
    setPagination(updater);
  };

  const handleSortingChange = (updater: any) => {
    setSorting(updater);
    setPagination(prev => ({ ...prev, pageIndex: 0 }));
  };

  const handleGlobalFilterChange = (value: string) => {
    setGlobalFilter(value);
  };

  const handleDateRangeChange = (newDateRange: DateRange) => {
    setDateRange(newDateRange || { from: undefined, to: undefined });
    setPagination(prev => ({ ...prev, pageIndex: 0 }));
  };

  const handleCustomerChange = (code: string | undefined) => {
    setSelectedCustId(code);
    setPagination(prev => ({ ...prev, pageIndex: 0 }));
  };

  const handleBranchChange = (ouCode: string | undefined) => {
    setSelectedOuCode(ouCode);
    setPagination(prev => ({ ...prev, pageIndex: 0 }));
  };

  const handleAlertFilterChange = (filters: AlertFilter) => {
    setActiveAlertFilters(filters);
    setPagination(prev => ({ ...prev, pageIndex: 0 }));
  };

  return (
    <DataTable 
      columns={deliveredColumns} 
      data={shipments}
      loading={loading}
      error={error}
      pagination={pagination}
      onPaginationChange={handlePaginationChange}
      sorting={sorting}
      onSortingChange={handleSortingChange}
      globalFilter={globalFilter}
      onGlobalFilterChange={handleGlobalFilterChange}
      dateRange={dateRange}
      onDateRangeChange={handleDateRangeChange}
      totalCount={totalCount}
      pageCount={pageCount}
      customerOptions={customerOptions}
      selectedCustId={selectedCustId}
      onCustomerChange={handleCustomerChange}
      branchOptions={branchOptions}
      selectedOuCode={selectedOuCode}
      onBranchChange={handleBranchChange}
      alertCards={alertCards}
      activeAlertFilters={activeAlertFilters}
      onAlertFilterChange={handleAlertFilterChange}
      isLoadingAlerts={isLoadingAlerts}
      alertSkeletonCount={2}
    />
  );
};

export default Delivered;
